import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  HourglassEmpty as PendingIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { jobsApi, usersApi, paymentsApi } from '../services/api';
import { Job, JobStats, User } from '../types';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<JobStats | null>(null);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Purchase credits state
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [tokenCost, setTokenCost] = useState<number>(0.10); // Default fallback value
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, jobsResponse, usersResponse] = await Promise.all([
        jobsApi.getStats(),
        jobsApi.getJobs(1, 5),
        usersApi.getUsers(),
      ]);
      const tokenCostResponse = await usersApi.getTokenCost();
      setTokenCost(tokenCostResponse.data.costPerToken);
      
      setStats(statsResponse.data);
      setRecentJobs(jobsResponse.data);
      setUsers(usersResponse.data);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCredits = async () => {
    if (!purchaseAmount || isNaN(Number(purchaseAmount)) || Number(purchaseAmount) <= 0) {
      return;
    }

    try {
      setPurchaseLoading(true);
      
      // Call the backend to create a Stripe checkout session
      const response = await paymentsApi.createCheckoutSession(Number(purchaseAmount));
      
      // Redirect to Stripe's payment page
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received from server');
      }
      
    } catch (err) {
      console.error('Purchase failed:', err);
      setError('Failed to initiate payment. Please try again.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'processing':
        return <RefreshIcon color="primary" />;
      case 'pending':
        return <PendingIcon color="action" />;
      default:
        return <ScheduleIcon color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'primary';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">
          Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setPurchaseDialogOpen(true)}
          sx={{ minWidth: 'fit-content' }}
        >
          Purchase Credits
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Jobs
            </Typography>
            <Typography variant="h4">
              {stats?.totalJobs || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending
            </Typography>
            <Typography variant="h4" color="warning.main">
              {stats?.pendingJobs || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Processing
            </Typography>
            <Typography variant="h4" color="primary.main">
              {stats?.processingJobs || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Completed
            </Typography>
            <Typography variant="h4" color="success.main">
              {stats?.completedJobs || 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Additional Stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Duration
            </Typography>
            <Typography variant="h4">
              {formatDuration(stats?.totalDuration || 0)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Average: {formatDuration(stats?.averageDuration || 0)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Success Rate
            </Typography>
            <Typography variant="h4">
              {stats?.totalJobs && stats?.completedJobs
                ? Math.round((stats.completedJobs / stats.totalJobs) * 100)
                : 0}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stats?.totalJobs && stats?.completedJobs
                ? (stats.completedJobs / stats.totalJobs) * 100
                : 0}
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* User Statistics */}
      {stats?.userStats && stats.userStats.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            User Activity
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {stats.userStats.slice(0, 6).map((userStat) => (
              <Card key={userStat.userId} sx={{ p: 2 }}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  {userStat.userName}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    Jobs
                  </Typography>
                  <Typography variant="caption">
                    {userStat.totalJobs}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    Duration
                  </Typography>
                  <Typography variant="caption">
                    {formatDuration(userStat.totalDuration)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="textSecondary">
                    Success Rate
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {userStat.totalJobs > 0 
                      ? Math.round((userStat.completedJobs / userStat.totalJobs) * 100)
                      : 0}%
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        </Paper>
      )}

      {/* Recent Recordings */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent Recordings
        </Typography>
        <List>
          {recentJobs.map((job) => {
            const user = users.find(u => u.id === job.userId);
            return (
              <ListItem key={job.id} divider>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  {getStatusIcon(job.status)}
                </Box>
                <ListItemText
                  primary={job.originalName}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {formatFileSize(job.fileSize)} • {formatDuration(job.duration)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Uploaded {formatDistanceToNow(new Date(job.uploadedAt), { addSuffix: true })} by {user?.name || 'Unknown User'}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={job.status}
                    color={getStatusColor(job.status) as any}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <IconButton size="small">
                    <PlayIcon />
                  </IconButton>
                  {job.status === 'completed' && (
                    <IconButton size="small">
                      <DownloadIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </Paper>

      {/* Purchase Credits Dialog */}
      <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)}>
        <DialogTitle>Purchase Credits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter the number of credits you would like to purchase. Credits are used for transcription and bookmark generation services.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Credits"
            type="number"
            fullWidth
            variant="outlined"
            value={purchaseAmount}
            onChange={(e) => setPurchaseAmount(e.target.value)}
            inputProps={{ min: 1 }}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Estimated cost: £{purchaseAmount ? (Number(purchaseAmount) * tokenCost).toFixed(2) : '0.00'} (£{tokenCost.toFixed(2)} per credit)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handlePurchaseCredits}
            variant="contained"
            disabled={!purchaseAmount || isNaN(Number(purchaseAmount)) || Number(purchaseAmount) <= 0 || purchaseLoading}
          >
            {purchaseLoading ? <CircularProgress size={20} /> : 'Purchase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        message="Payment successful! Your credits have been added to your account."
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Dashboard; 